/*
 * doc-store — owns the Y.Doc lifecycle for one draft.
 *
 * The hybrid offline-sync architecture (file-as-mental-model,
 * CRDT-as-data-layer) keeps one Y.Doc per draft, identified by the
 * UUID embedded in the Fountain title page (`Lastslate-ID:`). Two
 * providers attach to that doc:
 *
 *   • y-indexeddb (always)        — canonical local store, survives
 *                                   reload/quit without going through
 *                                   the main process. Replaces the
 *                                   legacy custom `yjs/` cache that
 *                                   was keyed by hash-of-filepath and
 *                                   dropped offline edits on reopen.
 *
 *   • @hocuspocus/provider (opt)  — network sync. Built-in offline
 *                                   queue + reconnect logic, so the
 *                                   editor never branches on
 *                                   online/offline. Attached only
 *                                   when the user has enabled cloud
 *                                   backup for this draft.
 *
 * Both providers operate on the SAME Y.Doc instance — they keep each
 * other in sync transparently via Yjs' update protocol. Callers
 * (app.js mount path) consume the handle's `doc` directly; do not
 * re-encode/re-create a Y.Doc downstream or persistence breaks.
 */

import * as Y from 'yjs';
import { IndexeddbPersistence } from 'y-indexeddb';
import { HocuspocusProvider, WebSocketStatus } from '@hocuspocus/provider';

const registry = new Map();

const IDB_PREFIX = 'lastslate:';

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const STATUS_OFFLINE_ONLY = 'offline-only';

export async function openDraft({ uuid, cloudBackup, refreshToken } = {}) {
  if (!uuid || !UUID_RE.test(String(uuid))) {
    throw new TypeError(`doc-store.openDraft: invalid uuid ${JSON.stringify(uuid)}`);
  }
  const canonical = String(uuid).toLowerCase();
  if (registry.has(canonical)) return registry.get(canonical);

  const doc = new Y.Doc();
  const idb = new IndexeddbPersistence(`${IDB_PREFIX}${canonical}`, doc);

  // Block until the local store has hydrated the Y.Doc. Without this,
  // ProseMirror would mount on an empty doc and the cached content
  // would appear "one tick later," which the pagination smoke tests
  // measure as a regression.
  await new Promise((resolve) => {
    if (idb.synced) resolve();
    else idb.once('synced', resolve);
  });

  const statusEmitter = createStatusEmitter();
  const authFailedEmitter = createStatusEmitter();
  let network = null;
  let lastStatus = STATUS_OFFLINE_ONLY;
  let refreshInFlight = null;

  function attachNetwork(opts) {
    if (network) return network;
    if (!opts || !opts.url || !opts.room) return null;
    const provider = new HocuspocusProvider({
      url: opts.url,
      name: opts.room,
      document: doc,
      token: opts.token || undefined,
      // We connect explicitly after wiring status handlers so the
      // first 'connecting' event is observable by listeners.
      connect: false,
    });
    provider.on('status', (event) => {
      lastStatus = event.status;
      statusEmitter.emit(event.status);
    });
    // Supabase access tokens expire ~1h; when they do, Hocuspocus
    // closes the connection with 4401 and emits this event. Without
    // a refresh, sync silently dies and the writer keeps typing
    // into a Y.Doc that goes nowhere. Re-fetch via the caller's
    // refreshToken callback and rebuild the provider against the
    // SAME Y.Doc so no edits are lost in flight.
    //
    // ONLY retry on reasons a fresh token might fix. The server's
    // AuthError reasons (see server/src/auth.js + index.js):
    //
    //   invalid_token / missing_token / auth_failed  — fixable by refresh
    //   free_tier_blocked / not_a_member            — won't be fixed
    //                                                  by re-fetching
    //                                                  the same JWT.
    //                                                  Retrying loops
    //                                                  forever; surface
    //                                                  to the renderer
    //                                                  via authFailedEmitter
    //                                                  and stop.
    provider.on('authenticationFailed', (event) => {
      const reason = event && event.reason ? event.reason : 'unknown';
      authFailedEmitter.emit(reason);
      const refreshable =
        reason === 'invalid_token' || reason === 'missing_token' || reason === 'auth_failed';
      if (!refreshable) return;
      if (typeof refreshToken !== 'function') return;
      if (refreshInFlight) return; // de-dup concurrent failures
      refreshInFlight = (async () => {
        try {
          const fresh = await refreshToken();
          if (!fresh || !fresh.token || !fresh.url || !fresh.room) return;
          if (!network) return;
          // Destroy the failed provider before attaching a new one;
          // network is closed-over so we have to clear it first.
          network.destroy();
          network = null;
          attachNetwork({ url: fresh.url, room: fresh.room, token: fresh.token });
        } catch (err) {
          console.warn('[doc-store] token refresh threw:', err && err.message);
        } finally {
          refreshInFlight = null;
        }
      })();
    });
    provider.connect();
    network = provider;
    return provider;
  }

  if (cloudBackup) attachNetwork(cloudBackup);

  const handle = {
    uuid: canonical,
    doc,
    get awareness() {
      return network ? network.awareness : null;
    },
    get status() {
      return network ? lastStatus : STATUS_OFFLINE_ONLY;
    },
    on: statusEmitter.on,
    off: statusEmitter.off,
    onAuthFailed: authFailedEmitter.on,
    offAuthFailed: authFailedEmitter.off,
    encodeState() {
      return Y.encodeStateAsUpdate(doc);
    },
    enableCloud(opts) {
      return attachNetwork(opts);
    },
    disableCloud() {
      if (!network) return;
      network.destroy();
      network = null;
      lastStatus = STATUS_OFFLINE_ONLY;
      statusEmitter.emit(STATUS_OFFLINE_ONLY);
    },
    async close() {
      registry.delete(canonical);
      if (network) {
        network.destroy();
        network = null;
      }
      // IndexeddbPersistence.destroy() returns void in current y-indexeddb
      // but we await defensively in case of future async impls.
      await idb.destroy();
      doc.destroy();
    },
  };

  registry.set(canonical, handle);
  return handle;
}

export function getDraft(uuid) {
  if (!uuid) return null;
  return registry.get(String(uuid).toLowerCase()) || null;
}

export async function closeDraft(uuid) {
  const handle = getDraft(uuid);
  if (handle) await handle.close();
}

export async function closeAll() {
  const handles = Array.from(registry.values());
  await Promise.all(handles.map((h) => h.close()));
}

// Erase a draft's local IndexedDB store entirely. Used by the
// migration v1→hybrid when the legacy yjs cache has been imported
// successfully and the source file is being deleted, and by the
// "remove cloud backup + local copy" UX path. Does NOT close an open
// draft — callers must close first.
export async function purgeLocal(uuid) {
  if (!uuid || !UUID_RE.test(String(uuid))) return;
  const canonical = String(uuid).toLowerCase();
  if (registry.has(canonical)) {
    throw new Error('doc-store.purgeLocal: draft is open; closeDraft first');
  }
  // y-indexeddb stores each doc in a separate IndexedDB database. The
  // simplest cross-browser way to drop it is via indexedDB.deleteDatabase.
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(`${IDB_PREFIX}${canonical}`);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => resolve(); // best-effort
  });
}

export const STATUS = {
  OfflineOnly: STATUS_OFFLINE_ONLY,
  Connecting: WebSocketStatus.Connecting,
  Connected: WebSocketStatus.Connected,
  Disconnected: WebSocketStatus.Disconnected,
};

function createStatusEmitter() {
  const listeners = new Set();
  return {
    emit(status) {
      for (const l of listeners) {
        try {
          l(status);
        } catch (err) {
          console.error('[doc-store] status listener threw:', err);
        }
      }
    },
    on(fn) {
      if (typeof fn === 'function') listeners.add(fn);
    },
    off(fn) {
      listeners.delete(fn);
    },
  };
}
